import ModelBaseClass from "@quasidea/oas-client-react/lib/ModelBaseClass";
import CashAccountVaultDetails from "../CashAccountVaultDetails";
import ModelProxyClass from "./ModelProxyClass";
import CashProcessingBatch from "../CashProcessingBatch";
import CollectionLocationActivity from "../CollectionLocationActivity";
import Person from "../Person";


/**
 * @class CashAccountVaultDetailsBase
 * @extends ModelBaseClass
 * @property {string} address1
 * @property {string} address2
 * @property {string} city
 * @property {string} state
 * @property {string} zip
 * @property {number} vaultTechnicianPersonsCount (integer) only set if NOT called via CashAccount::get()
 * @property {CashProcessingBatch} latestCashProcessingBatch only set on CashAccount::get() -- this will be either the latest completed batch, OR the current batch that is currently in process (e.g. started, but not yet ended)
 * @property {[CollectionLocationActivity]} unprocessedDepositCollectionLocationActivities only set on CashAccount::get() -- this will be all unprocessed deposits organized by location/gamingday
 * @property {[Person]} vaultTechnicianPersons only set on CashAccount::get() -- the list of VaultTechnician Persons that have access to this Vault
 */
class CashAccountVaultDetailsBase extends ModelBaseClass {
	/**
	 * @type {string} address1
	 */
	address1;
	/**
	 * @type {string} address2
	 */
	address2;
	/**
	 * @type {string} city
	 */
	city;
	/**
	 * @type {string} state
	 */
	state;
	/**
	 * @type {string} zip
	 */
	zip;
	/**
	 * only set if NOT called via CashAccount::get()
	 * @type {number} vaultTechnicianPersonsCount (integer)
	 */
	vaultTechnicianPersonsCount;
	/**
	 * only set on CashAccount::get() -- this will be either the latest completed batch, OR the current batch that is currently in process (e.g. started, but not yet ended)
	 * @type {CashProcessingBatch} latestCashProcessingBatch
	 */
	latestCashProcessingBatch;
	/**
	 * only set on CashAccount::get() -- this will be all unprocessed deposits organized by location/gamingday
	 * @type {[CollectionLocationActivity]} unprocessedDepositCollectionLocationActivities
	 */
	unprocessedDepositCollectionLocationActivities;
	/**
	 * only set on CashAccount::get() -- the list of VaultTechnician Persons that have access to this Vault
	 * @type {[Person]} vaultTechnicianPersons
	 */
	vaultTechnicianPersons;

	/**
	 * Instantiates a new instance of CashAccountVaultDetails based on the generic object being passed in (typically from a JSON object)
	 * @param {object} genericObject
	 * @return {CashAccountVaultDetails}
	 */
	static create(genericObject) {
		const newCashAccountVaultDetails = new CashAccountVaultDetails();
		newCashAccountVaultDetails.instantiate(_modelDefinition, genericObject, ModelProxyClass.createByClassName);
		return newCashAccountVaultDetails;
	}

	/**
	 * Instantiates a new array of CashAccountVaultDetails based on the generic array being passed in (typically from a JSON array)
	 * @param {[object]} genericArray
	 * @return {[CashAccountVaultDetails]}
	 */
	static createArray(genericArray) {
		if (genericArray === null) {
			return null;
		}

		const newCashAccountVaultDetailsArray = [];
		genericArray.forEach(genericObject => {
			newCashAccountVaultDetailsArray.push(CashAccountVaultDetails.create(genericObject));
		});
		return newCashAccountVaultDetailsArray;
	}
}

const _modelDefinition = [
	ModelBaseClass.createModelProperty('address1', 'string'),
	ModelBaseClass.createModelProperty('address2', 'string'),
	ModelBaseClass.createModelProperty('city', 'string'),
	ModelBaseClass.createModelProperty('state', 'string'),
	ModelBaseClass.createModelProperty('zip', 'string'),
	ModelBaseClass.createModelProperty('vaultTechnicianPersonsCount', 'integer'),
	ModelBaseClass.createModelProperty('latestCashProcessingBatch', 'CashProcessingBatch'),
	ModelBaseClass.createModelProperty('unprocessedDepositCollectionLocationActivities', '[CollectionLocationActivity]'),
	ModelBaseClass.createModelProperty('vaultTechnicianPersons', '[Person]'),
];

export default CashAccountVaultDetailsBase;
